import { Component, h, Prop, Event, EventEmitter, Element, State, Listen, Method } from '@stencil/core';
import { getObjectByKeyValue, removeObjByKeyValue } from '../../helpers/utils';
import { i18n } from '../../utils/I18n';
import {DATA_STORE_KEY_PASSENGERS, dataStore} from '../../global/DataStore';
import { PassangerType } from './PassangerType';
import { canAddPassangerObject, canAddPassangerBike, canAddPassangerPerson, getBirthdateForPassangertype, getDefaultPassanger, removeBikeIfMoreBikesThanPassangers, getPassangersFromStore, isHumanPassangerType } from './passanger-utils';
import { IReductionCardFTO, PrebookObjectV4 } from '../../api/Ticketshop';
import { ReductionCardsApi } from '../nj-reduction-cards/ReductionCardsApi';
import { Gender } from "../../utils/genderUtilities";

export interface SelectedUser extends PrebookObjectV4 {
	njUserName: string;
	njPassangerType: PassangerType;
	njBirthDate?: string;
	njCards: any[];
	njChallengedOptions?: { wheelchair?: boolean, attendant?: boolean, dog?: boolean };
}

@Component({
	tag: 'nj-passangers',
	styleUrl: 'nj-passangers.scss',
	shadow: true,
})
export class NjPassangers {

	reductionCardsElement: HTMLNjReductionCardsElement;
	challengedOptionsElement: HTMLNjChallengedOptionsElement;
	defaultUser: SelectedUser[] = [];
	passangerInputField: HTMLInputElement;
	cardsChallenged: IReductionCardFTO[] = [];
	maximumNumberOfPassengersDialogue: HTMLNjDialogElement;

	@Element() el: HTMLElement;
	@State() dataChanged: boolean;
	@State() counterAdults: number;
	@State() counterChildren: number;
	@State() counterSmallChildrenWithSeatFemale: number;
	@State() counterSmallChildrenWithSeatMale: number;
	@State() counterSmallChildrenFemale: number;
	@State() counterSmallChildrenMale: number;
	@State() counterAdultsFemale: number;
	@State() counterAdultsMale: number;
	@State() counterAdultsDiverse: number;
	@State() counterChildrenFemale: number;
	@State() counterChildrenMale: number;
	@State() counterChildren10To14Female: number;
	@State() counterChildren10To14Male: number;
	@State() counterBikes: number;

	@State() selectedUsers: SelectedUser[] = [];
	@State() cardData: any[] = [];
	@State() maxReachedPersons: boolean = false;
	@State() maxReachedBikes: boolean = false;
	@State() maxReachedObjects: boolean = false;

	@Prop({ mutable: true, reflect: true }) visible: boolean = false;

	@Event() passangerTextChanged: EventEmitter;
	@Event() passangerChanged: EventEmitter;

    private dialogElements: HTMLNjDialogElement[]
    private dialogTriggerElement?: HTMLElement;

    private focusableElements?: HTMLElement[];
    private previousTabIndexes?: Map<HTMLElement, string | null> = new Map();

	componentWillLoad() {
		this.passangerInputField = this.el.parentElement.querySelector('#nj-passanger-field input') as HTMLInputElement;
		this.selectedUsers = dataStore().getSessionData(DATA_STORE_KEY_PASSENGERS);
		this.cardsChallenged = ReductionCardsApi.getCardsChallenged();
		this.dataChanged = false;
		if (!this.selectedUsers) {
			this.selectedUsers = dataStore().getLocalData(DATA_STORE_KEY_PASSENGERS, null);
		}

		if (!this.selectedUsers) {
			this.selectedUsers = this.defaultUser;
		}

		this.handleDataChange();
	}

	async componentDidLoad() {
        this.reductionCardsElement = this.el.shadowRoot.querySelector('nj-reduction-cards') as HTMLNjReductionCardsElement;
		this.challengedOptionsElement = this.el.shadowRoot.querySelector('nj-challenged-options') as HTMLNjChallengedOptionsElement;
		this.maximumNumberOfPassengersDialogue = this.el.shadowRoot.querySelector("#dialogueMaximumNumberOfPassengersReached") as HTMLNjDialogElement;

        this.dialogElements = Array.from(this.el.shadowRoot.querySelectorAll('nj-dialog')) as HTMLNjDialogElement[];

        const elements: NodeListOf<HTMLElement> = this.el.shadowRoot.querySelectorAll(`
            a[href]:not([disabled]),
            button:not([disabled]),
            textarea:not([disabled]),
            input[type="text"]:not([disabled]),
            input[type="radio"]:not([disabled]),
            input[type="checkbox"]:not([disabled]),
            select:not([disabled])
        `);

        this.focusableElements = Array.from(elements).filter(element => !element.closest('nj-dialog'));
        this.storePreviousTabIndexes();

    }

	@Listen('njPassengersChanged', {target: 'window'})
	njPassengersChangedHandler() {
		// Passengers were changed from some other component, refresh them
		this.selectedUsers = getPassangersFromStore();
		this.handleDataChange();
	}

    @Listen('blur')
    handleBlur() {
        //this.closeElement();
    }

	@Event() passangersDialogClosed: EventEmitter;
	passangersDialogClosedHandler(info?:string) {
		if (this.dataChanged === true){
			this.passangersDialogClosed.emit({
				name: 'passangers',
				info: info
			});
			this.dataChanged = false;
		}

	}

	@Listen('passengerOptionsChanged')
	passengerOptionsChangedHandler(event: CustomEvent) {
		let oldUserObj = getObjectByKeyValue(this.selectedUsers, 'njUserName', event.detail.njUserName);
		if (oldUserObj) {
			let indexOldUserObj = this.selectedUsers.indexOf(oldUserObj);
			this.selectedUsers[indexOldUserObj] = event.detail;

			this.selectedUsers = [...this.selectedUsers];
			this.handleDataChange();
		}
	}

	@Listen('keydown')
	async onKeydown(event: KeyboardEvent) {
		if (event.key === 'Tab' && !event.shiftKey) {
				const previousFocusedElement = event.composedPath()[0] as HTMLElement

				if (this.focusableElements[this.focusableElements.length - 1] == previousFocusedElement) {
					this.hide()
				}

			}
	}

    @Listen('dialogOpen', {target: 'window'})
	dialogOpenHandler(event: CustomEvent) {
        if (this.dialogElements.includes(event.detail.element)) {
            this.disableTabindex();
		}
	}

    @Listen('dialogClosed', {target: 'window'})
	dialogClosedHandler(event: CustomEvent) {
        if (this.dialogElements.includes(event.detail.element)) {
            this.resetTabindex();
            this.dialogTriggerElement?.focus();
		}
	}

	@Method()
	async toggle() {
		if(this.visible) {
			this.hide();
		}
		else {
			this.show();
		}
	}

	@Method()
	async isVisible(): Promise<boolean> {
		return Promise.resolve(this.visible);
	}

	@Method()
	async show() {
		this.openElement();
	}

	@Method()
	async hide() {
		this.closeElement();
	}

    private readonly openElement = () => {
        this.el.style.display = 'inline-block';
		this.visible = true;
		this.dataChanged = false;
		var rect = this.passangerInputField.getBoundingClientRect();
		this.el.style.top = rect.height - 2 + 'px';
		this.el.style.left = '0';
    }

    private readonly closeElement = () => {
        this.el.style.display = 'none';
		this.visible = false;
		this.el.shadowRoot.querySelector('input').blur();
		this.passangersDialogClosedHandler("hide");

        this.dialogElements.forEach(el => el.hide());
    }

    private readonly storePreviousTabIndexes = () => {
        this.focusableElements.forEach((el: HTMLElement) => {
            const previousTabIndex = el.getAttribute('tabindex');
            this.previousTabIndexes.set(el, previousTabIndex);
        });
    }

    private readonly disableTabindex = () => {
        this.focusableElements.forEach((focusableElement: HTMLElement) => {
            if (this.el.shadowRoot.contains(focusableElement)) {
                focusableElement.setAttribute('tabindex', '-1');
            }
        });
    }

    private readonly resetTabindex = () => {
        this.focusableElements.forEach((focusableElement: HTMLElement) => {
            if (this.el.shadowRoot.contains(focusableElement)) {
                const previousTabIndex = this.previousTabIndexes.get(focusableElement);

                if (previousTabIndex) {
                    focusableElement.setAttribute('tabindex', previousTabIndex);
                } else {
                    focusableElement.removeAttribute('tabindex');
                }
            }
        });
    }

	openReductionCardsDialog(e: Event, user: SelectedUser) {
		this.reductionCardsElement.setUser(user);
		const dialogEl = this.el.shadowRoot.querySelector('.js-dialog-reduction-cards') as HTMLNjDialogElement;

        // e.target not working because of shadow component
        this.dialogTriggerElement = e.composedPath()[0] as HTMLElement;
        dialogEl.toggle();
	}

	openChallangedOptionsDialog(e: Event, user: SelectedUser) {
		this.challengedOptionsElement.setUserAndLimits(user, this.maxReachedObjects, this.maxReachedPersons);
		const dialogEl = this.el.shadowRoot.querySelector('.js-dialog-challenged-options') as HTMLNjDialogElement;
		this.dialogTriggerElement = e.composedPath()[0] as HTMLElement;
        dialogEl.toggle();
	}

	handleDataChange() {
		this.maxReachedPersons = !canAddPassangerPerson(this.selectedUsers);
		this.maxReachedBikes = !canAddPassangerBike(this.selectedUsers);
		this.maxReachedObjects = !canAddPassangerObject(this.selectedUsers);

		removeBikeIfMoreBikesThanPassangers(this.selectedUsers);

		this.counterAdults = this.selectedUsers.filter(passanger => passanger.njPassangerType === PassangerType.Adult).length;
		this.counterAdultsFemale = this.selectedUsers.filter(passanger => passanger.njPassangerType === PassangerType.Adult && passanger.gender === "female").length;
		this.counterAdultsMale = this.selectedUsers.filter(passanger => passanger.njPassangerType === PassangerType.Adult && passanger.gender === "male").length;
		this.counterAdultsDiverse = this.selectedUsers.filter(passanger => passanger.njPassangerType === PassangerType.Adult && passanger.gender === "diverse").length;
		this.counterChildren =
			this.selectedUsers.filter(passanger => passanger.njPassangerType === PassangerType.Child).length
			+ this.selectedUsers.filter(passanger => passanger.njPassangerType === PassangerType.TenToFourteenYearOldChild).length;
		this.counterChildren10To14Female = this.selectedUsers.filter(passanger => passanger.njPassangerType === PassangerType.TenToFourteenYearOldChild && passanger.gender === "female").length;
		this.counterChildren10To14Male = this.selectedUsers.filter(passanger => passanger.njPassangerType === PassangerType.TenToFourteenYearOldChild && passanger.gender === "male").length;
		this.counterChildrenFemale = this.selectedUsers.filter(passanger => passanger.njPassangerType === PassangerType.Child && passanger.gender === "female").length;
		this.counterChildrenMale = this.selectedUsers.filter(passanger => passanger.njPassangerType === PassangerType.Child && passanger.gender === "male").length;
		this.counterSmallChildrenFemale = this.selectedUsers.filter(passanger => passanger.njPassangerType === PassangerType.SmallChild && passanger.gender === "female").length;
		this.counterSmallChildrenMale = this.selectedUsers.filter(passanger => passanger.njPassangerType === PassangerType.SmallChild && passanger.gender === "male").length;
		this.counterSmallChildrenWithSeatFemale = this.selectedUsers.filter(passanger => passanger.njPassangerType === PassangerType.SmallChildSeat && passanger.gender === "female").length;
		this.counterSmallChildrenWithSeatMale = this.selectedUsers.filter(passanger => passanger.njPassangerType === PassangerType.SmallChildSeat && passanger.gender === "male").length;

		this.counterBikes = this.selectedUsers.filter(passanger => passanger.njPassangerType === PassangerType.Bike).length;

		if (this.selectedUsers != dataStore().getSessionData(DATA_STORE_KEY_PASSENGERS)) {
			this.dataChanged = true;
			this.passangerChanged.emit(this.selectedUsers);
			this.passangerTextChanged.emit(this.getPassangerTxt());

			dataStore().setSessionData(DATA_STORE_KEY_PASSENGERS, this.selectedUsers);
		}

	}

	countUp(event: Event, passangerType: PassangerType, gender?: Gender) {
		event.preventDefault();
		event.stopPropagation();

		if (!this.selectedUsers) { return; }
		if (this.maxReachedBikes && passangerType === PassangerType.Bike) { return; }
		if (this.maxReachedPersons && passangerType !== PassangerType.Bike) {
			this.dialogTriggerElement = event.composedPath()[0] as HTMLElement;
            this.maximumNumberOfPassengersDialogue.show();
			return;
		}
		if (this.maxReachedObjects) { return; }

		const newPassanger: SelectedUser = getDefaultPassanger(passangerType)[0];

		if (isHumanPassangerType(passangerType)) {
			newPassanger.cards = [];
			newPassanger.birthDate = getBirthdateForPassangertype(passangerType);
			newPassanger.gender = gender ? gender : null;
		}

		this.selectedUsers = [...this.selectedUsers, newPassanger];

		this.handleDataChange();
	}

	countDown(event: Event, passangerType: PassangerType, gender?: Gender) {
		event.preventDefault();
		event.stopPropagation();

		const arrKeys = this.selectedUsers.map(passanger => passanger.njPassangerType);
		let lastIndex = arrKeys.lastIndexOf(passangerType);

		if (gender) {
			lastIndex = -1;
			for (let i = this.selectedUsers.length - 1; i >= 0; i--) {
				if (this.selectedUsers[i].gender === gender && this.selectedUsers[i].njPassangerType === passangerType) {
					lastIndex = i;
					break;
				}
			}
		}

		if (lastIndex > -1) {
			this.selectedUsers.splice(lastIndex, 1);
			this.selectedUsers = [...this.selectedUsers];
		}

		this.handleDataChange();
	}

	removeUser(user: SelectedUser) {
		this.selectedUsers = [...removeObjByKeyValue(this.selectedUsers, 'njUserName', user.njUserName)];
		this.handleDataChange();
	}

	getPassangerTxt() {
		if (this.selectedUsers.length <= 0) {
			return i18n.getMessage("passengers.noSelection", "Keine Reisenden! Sie müssen mindestens einen Reisenden zu Ihrer Reise hinzufügen.");
		}

		let inputValue = '';

		if (this.counterAdults > 0) {
			inputValue += this.counterAdults + ' ' + (this.counterAdults === 1 ? i18n.getMessage("passengers.adult", "Erwachsene:r") : i18n.getMessage("passengers.adults", "Erwachsene"));
		}

		if (this.counterChildren > 0) {
			inputValue += inputValue.length > 0 ? ' | ' : '';
			inputValue += this.counterChildren + ' ' + (this.counterChildren === 1 ? i18n.getMessage("passengers.child.passenger-field", "Kind") : i18n.getMessage("passengers.children", "Kinder"));
		}

		let smallChildrenAmount = this.counterSmallChildrenMale + this.counterSmallChildrenFemale + this.counterSmallChildrenWithSeatMale + this.counterSmallChildrenWithSeatFemale;
		if (smallChildrenAmount > 0) {
			inputValue += inputValue.length > 0 ? ' | ' : '';
			inputValue += smallChildrenAmount + ' ' + (smallChildrenAmount === 1 ? i18n.getMessage("passengers.small-child", "Kleinkind") : i18n.getMessage("passengers.small-children", "Kleinkinder"));
		}

		if (this.counterBikes > 0) {
			inputValue += inputValue.length > 0 ? ' | ' : '';
			inputValue += this.counterBikes + ' ' + (this.counterBikes === 1 ? i18n.getMessage("passengers.bike", "Fahrrad") : i18n.getMessage("passengers.bikes", "Fahrräder"));
		}

		return inputValue;
	}

	renderDiversePassengerDisclaimer() {
		if (this.isAnyPassengerDiverse(this.selectedUsers)) {
			return (<p class="diverse-passenger-disclaimer">{i18n.getMessage("passengers.diversePassengerInformation")}</p>);
		}
		return (<span></span>);
	}

	isAnyPassengerDiverse(travelGroup: SelectedUser[]): boolean {
		for (const passenger of travelGroup) {
			if (
				passenger !== undefined && passenger !== null
				&& passenger.gender === "diverse"
				&& (
					passenger.njPassangerType === PassangerType.Adult
					// || selectedUser.njPassangerType === PassangerType.Child
					// || selectedUser.njPassangerType === PassangerType.SmallChild
					// || selectedUser.njPassangerType === PassangerType.SmallChildSeat
					// || selectedUser.njPassangerType === PassangerType.TenToFourteenYearOldChild
				)
			) {
				return true;
			}
		}
		return false;
	}

	renderSelectedUsersByUsertype(passangerType: PassangerType) {
		return this.selectedUsers
			.filter(passanger => passanger.njPassangerType === passangerType)
			.map((selectedUser, i) => (
				<div>
					<div class="selected-users">

						<div class="selected-user">
							{i + 1}. {i18n.getMessage(`passengers.${selectedUser.njPassangerType}`, 'Reisender')}
							{selectedUser.njPassangerType === PassangerType.Adult ? ', ' + i18n.getMessage(`passengers.${selectedUser.gender}`) : ''}
							{selectedUser.njPassangerType === PassangerType.Child ? ', ' + i18n.getMessage(`passengers.child-${selectedUser.gender}`) : ''}
							{selectedUser.njPassangerType === PassangerType.TenToFourteenYearOldChild ? ', ' + i18n.getMessage(`passengers.child-10-to-14-${selectedUser.gender}`) : ''}
							{selectedUser.njPassangerType === PassangerType.SmallChildSeat || selectedUser.njPassangerType === PassangerType.SmallChild ? ', ' + i18n.getMessage(`passengers.small-child.${selectedUser.gender}`) : ''}
							<button type="button" class="remove-btn" onClick={() => this.removeUser(selectedUser)}>
								<delete-icon height={20} width={20} aria-hidden="true" />
								<span class="screen-reader-text">{i18n.getMessage(`passengers.remove.selected.user`)}</span>

							</button>
							<div class="rendered-cards">
								{this.renderCardData(selectedUser)}
								<button type="button" class="button-link" onClick={(e) => this.openReductionCardsDialog(e, selectedUser)}>
									{i18n.getMessage("passengers.addReductionCards", "Ermäßigungskarten")}
								</button>
								<br />
								<button type="button" class="button-link" onClick={(e) => this.openChallangedOptionsDialog(e, selectedUser)}>
									{i18n.getMessage('passengers.addChallengedOptions', 'Reisen mit Behinderung')}
								</button>
							</div>
						</div>
					</div>
				</div>
			));
	}

	renderCardData(user: SelectedUser) {
		return (
			<ul class="rendered-cards__list">
				{user.njCards.map(card => {
					return <li>{i18n.getMessage('ts.reductionCard.name.' + card.code, 'Karte ' + card.code, null, card.cardName)}</li>;
				})}
				{user.njChallengedOptions?.wheelchair ? <li>{i18n.getMessage('passengers.wheelchair', 'Rollstuhl')}</li> : null}
				{user.njChallengedOptions?.attendant ? <li>{i18n.getMessage('passengers.attendant', 'Begleitperson')}</li> : null}
				{user.njChallengedOptions?.dog ? <li>{i18n.getMessage('passengers.dog', 'Assistenzhund')}</li> : null}
			</ul>
		);
	}

	render() {
		return (
			<div onClick={(e: Event) => e.stopPropagation()} class="rootContainer">
				<div class="counter">
					<p
						class="diverse-passenger-disclaimer"
						hidden={!this.isAnyPassengerDiverse(this.selectedUsers)}
					>
						{i18n.getMessage("passengers.diversePassengerInformation")}
					</p>
					<div class="counter__wrapper">
						<div>
							<span class="counter__label">{i18n.getMessage("passengers.adults", "Erwachsene")}</span>
							{i18n.getMessage("passengers.adultsFromAge", "(ab 15 Jahre)")}
						</div>
						<div>
							<div class="counter__controler">
								<span>{i18n.getMessage("passengers.female", "Frau")}: </span>
								<button class={this.counterAdultsFemale === 0 ? 'disabled' : ''}
										onClick={(event) => this.countDown(event, PassangerType.Adult, "female")}>
									<minus-icon aria-hidden="true" />
									<span class="screen-reader-text">
										{i18n.getMessage("passengers.remove.adult.female", "Erwachsene:r, weiblich entfernen")}
									</span>
								</button>
								<span>{this.counterAdultsFemale}</span>
								<button class={this.maxReachedPersons ? 'disabled' : ''}
										onClick={(e) => this.countUp(e, PassangerType.Adult, "female")}>
									<add-icon aria-hidden="true" />
									<span class="screen-reader-text">
										{i18n.getMessage("passengers.add.adult.female", "Erwachsene:r, weiblich hinzufügen")}
									</span>
								</button>
							</div>

							<div class="counter__controler">
								<span>{i18n.getMessage("passengers.male", "Mann")}: </span>
								<button class={this.counterAdultsMale === 0 ? 'disabled' : ''}
										onClick={(event) => this.countDown(event, PassangerType.Adult, "male")}>
									<minus-icon aria-hidden="true" />
									<span class="screen-reader-text">
										{i18n.getMessage("passengers.remove.adult.male", "Erwachsene:r, männlich entfernen")}
									</span>
								</button>
								<span>{this.counterAdultsMale}</span>
								<button class={this.maxReachedPersons ? 'disabled' : ''}
										onClick={(e) => this.countUp(e, PassangerType.Adult, "male")}>
									<add-icon aria-hidden="true" />
									<span class="screen-reader-text">
										{i18n.getMessage("passengers.add.adult.male", "Erwachsene:r, männlich  hinzufügen")}
									</span>
								</button>
							</div>

							{<div class="counter__controler">
								<span>{i18n.getMessage("passengers.diverse")}: </span>
								<button class={this.counterAdultsDiverse === 0 ? 'disabled' : ''}
										onClick={(event) => this.countDown(event, PassangerType.Adult, "diverse")}>
									<minus-icon aria-hidden="true" />
									<span class="screen-reader-text">
										{i18n.getMessage("passengers.remove.adult.diverse", "Erwachsene:r, divers / keine Angabe entfernen")}
									</span>
								</button>
								<span>{this.counterAdultsDiverse}</span>
								<button class={this.maxReachedPersons ? 'disabled' : ''}
										onClick={(e) => this.countUp(e, PassangerType.Adult, "diverse")}>
									<add-icon aria-hidden="true" />
									<span class="screen-reader-text">
										{i18n.getMessage("passengers.add.adult.diverse", "Erwachsene:r, divers / keine Angabe hinzufügen")}
									</span>
								</button>
							</div>}
						</div>
					</div>
					{this.renderSelectedUsersByUsertype(PassangerType.Adult)}
				</div>
				<div class="counter">
					<div class="counter__wrapper">
						<div>
							<span class="counter__label">{i18n.getMessage('passengers.children', 'Kinder')}</span>
							{i18n.getMessage('passengers.childrenFromAge10To14', '(10 bis 14 Jahre)')}
						</div>
						<div>
							<div class="counter__controler">
								<span>{i18n.getMessage('passengers.child-female')}: </span>
								<button class={this.counterChildren10To14Female === 0 ? 'disabled' : ''}
										onClick={(event) => this.countDown(event, PassangerType.TenToFourteenYearOldChild, 'female')}>
									<minus-icon aria-hidden="true" />
									<span class="screen-reader-text">
										{i18n.getMessage("passengers.remove.childrenFromAge10To14.female", "Kind (10 bis 14 Jahre), weiblich entfernen")}
									</span>
								</button>
								<span>{this.counterChildren10To14Female}</span>
								<button class={this.maxReachedPersons ? 'disabled' : ''}
										onClick={(e) => this.countUp(e, PassangerType.TenToFourteenYearOldChild, 'female')}>
									<add-icon aria-hidden="true" />
									<span class="screen-reader-text">
										{i18n.getMessage("passengers.add.childrenFromAge10To14.female", "Kind (10 bis 14 Jahre), weiblich hinzufügen")}
									</span>
								</button>
							</div>

							<div class="counter__controler">
								<span>{i18n.getMessage("passengers.child-male", "Bursche")}: </span>
								<button class={this.counterChildren10To14Male === 0 ? 'disabled' : ''}
										onClick={(event) => this.countDown(event, PassangerType.TenToFourteenYearOldChild, 'male')}>
									<minus-icon aria-hidden="true" />
									<span class="screen-reader-text">
										{i18n.getMessage("passengers.remove.childrenFromAge10To14.male", "Kind (10 bis 14 Jahre), männlich entfernen")}
									</span>
								</button>
								<span>{this.counterChildren10To14Male}</span>
								<button class={this.maxReachedPersons ? 'disabled' : ''}
										onClick={(e) => this.countUp(e, PassangerType.TenToFourteenYearOldChild, 'male')}>
									<add-icon aria-hidden="true" />
									<span class="screen-reader-text">
										{i18n.getMessage("passengers.add.childrenFromAge10To14.male", "Kind (10 bis 14 Jahre), männlich hinzufügen")}
									</span>
								</button>
							</div>
						</div>
					</div>
					{this.renderSelectedUsersByUsertype(PassangerType.TenToFourteenYearOldChild)}
				</div>
				<div class="counter">
					<div class="counter__wrapper">
						<div>
							<span class="counter__label">{i18n.getMessage('passengers.children', 'Kinder')}</span>
							{i18n.getMessage('passengers.childrenFromAge', '(6 bis 9 Jahre)')}
						</div>
						<div>
							<div class="counter__controler">
								<span>{i18n.getMessage('passengers.child-female')}: </span>
								<button class={this.counterChildrenFemale === 0 ? 'disabled' : ''}
										onClick={(event) => this.countDown(event, PassangerType.Child, 'female')}>
									<minus-icon aria-hidden="true" />
									<span class="screen-reader-text">
										{i18n.getMessage("passengers.remove.childrenFromAge6To9.female", "Kind (6 bis 9 Jahre), weiblich entfernen")}
									</span>
								</button>
								<span>{this.counterChildrenFemale}</span>
								<button class={this.maxReachedPersons ? 'disabled' : ''}
										onClick={(e) => this.countUp(e, PassangerType.Child, 'female')}>
									<add-icon aria-hidden="true" />
									<span class="screen-reader-text">
										{i18n.getMessage("passengers.add.childrenFromAge6To9.female", "Kind (6 bis 9 Jahre), weiblich hinzufügen")}
									</span>
								</button>
							</div>

							<div class="counter__controler">
								<span>{i18n.getMessage("passengers.child-male", "Bursche")}: </span>
								<button class={this.counterChildrenMale === 0 ? 'disabled' : ''}
										onClick={(event) => this.countDown(event, PassangerType.Child, 'male')}>
									<minus-icon aria-hidden="true" />
									<span class="screen-reader-text">
										{i18n.getMessage("passengers.remove.childrenFromAge6To9.male", "Kind (6 bis 9 Jahre), männlich entfernen")}
									</span>
								</button>
								<span>{this.counterChildrenMale}</span>
								<button class={this.maxReachedPersons ? 'disabled' : ''}
										onClick={(e) => this.countUp(e, PassangerType.Child, 'male')}>
									<add-icon aria-hidden="true" />
									<span class="screen-reader-text">
										{i18n.getMessage("passengers.add.childrenFromAge6To9.male", "Kind (6 bis 9 Jahre), männlich hinzufügen")}
									</span>
								</button>
							</div>
						</div>
					</div>
					{this.renderSelectedUsersByUsertype(PassangerType.Child)}
				</div>
				<div class="counter">
					<div class="counter__wrapper">
						<div>
						<span
							class="counter__label">{i18n.getMessage("passengers.smallChildrenWithSeat", "Kleinkinder mit eigenem Platz (kostenpflichtig)")}</span>
							{i18n.getMessage("passengers.smallChildrenAge", "(0 bis 5 Jahre)")}
						</div>
						<div>
							<div class="counter__controler">
								<span>{i18n.getMessage("passengers.small-child.female", "Sunday Girl")}: </span>
								<button class={this.counterSmallChildrenWithSeatFemale === 0 ? 'disabled' : ''}
										onClick={(event) => this.countDown(event, PassangerType.SmallChildSeat, "female")}>
									<minus-icon aria-hidden="true" />
									<span class="screen-reader-text">
										{i18n.getMessage("passengers.remove.smallChildrenWithSeat.female", "Kleinkind (0 bis 5 Jahre) mit Sitzplatz, weiblich entfernen")}
									</span>
								</button>
								<span>{this.counterSmallChildrenWithSeatFemale}</span>
								<button class={this.maxReachedPersons ? 'disabled' : ''}
										onClick={(e) => this.countUp(e, PassangerType.SmallChildSeat, "female")}>
									<add-icon aria-hidden="true" />
									<span class="screen-reader-text">
										{i18n.getMessage("passengers.add.smallChildrenWithSeat.female", "Kleinkind (0 bis 5 Jahre) mit Sitzplatz, weiblich hinzufügen")}
									</span>
								</button>
							</div>
							<div class="counter__controler">
								<span>{i18n.getMessage("passengers.small-child.male", "Bursche")}: </span>
								<button class={this.counterSmallChildrenWithSeatMale === 0 ? 'disabled' : ''}
										onClick={(event) => this.countDown(event, PassangerType.SmallChildSeat, "male")}>
									<minus-icon aria-hidden="true" />
									<span class="screen-reader-text">
										{i18n.getMessage("passengers.remove.smallChildrenWithSeat.male", "Kleinkind (0 bis 5 Jahre) mit Sitzplatz, männlich entfernen")}
									</span>
								</button>
								<span>{this.counterSmallChildrenWithSeatMale}</span>
								<button class={this.maxReachedPersons ? 'disabled' : ''}
										onClick={(e) => this.countUp(e, PassangerType.SmallChildSeat, "male")}>
									<add-icon aria-hidden="true" />
									<span class="screen-reader-text">
										{i18n.getMessage("passengers.add.smallChildrenWithSeat.male", "Kleinkind (0 bis 5 Jahre) mit Sitzplatz, männlich hinzufügen")}
									</span>
								</button>
							</div>
						</div>

					</div>
					{this.renderSelectedUsersByUsertype(PassangerType.SmallChildSeat)}
				</div>
				<div class="counter">
					<div class="counter__wrapper">
						<div>
						<span
							class="counter__label">{i18n.getMessage("passengers.smallChildrenWithoutSeat", "Kleinkinder Platz geteilt mit Erwachsenem")}</span>
							{i18n.getMessage("passengers.smallChildrenAge", "(0 bis 5 Jahre)")}
						</div>
						<div>
							<div class="counter__controler">
								<span>{i18n.getMessage("passengers.small-child.female", "Sunday Girl")}: </span>
								<button class={this.counterSmallChildrenFemale === 0 ? 'disabled' : ''}
										onClick={(event) => this.countDown(event, PassangerType.SmallChild, "female")}>
									<minus-icon aria-hidden="true" />
									<span class="screen-reader-text">
										{i18n.getMessage("passengers.remove.smallChildrenWithoutSeat.female", "Kleinkind (0 bis 5 Jahre) ohne Sitzplatz, weiblich entfernen")}
									</span>
								</button>
								<span>{this.counterSmallChildrenFemale}</span>
								<button class={this.maxReachedPersons ? 'disabled' : ''}
										onClick={(e) => this.countUp(e, PassangerType.SmallChild, "female")}>
									<add-icon aria-hidden="true" />
									<span class="screen-reader-text">
										{i18n.getMessage("passengers.add.smallChildrenWithoutSeat.female", "Kleinkind (0 bis 5 Jahre) ohne Sitzplatz, weiblich hinzufügen")}
									</span>
								</button>
							</div>
							<div class="counter__controler">
								<span>{i18n.getMessage("passengers.small-child.male", "Bursche")}: </span>
								<button class={this.counterSmallChildrenMale === 0 ? 'disabled' : ''}
										onClick={(event) => this.countDown(event, PassangerType.SmallChild, "male")}>
									<minus-icon aria-hidden="true" />
									<span class="screen-reader-text">
										{i18n.getMessage("passengers.remove.smallChildrenWithoutSeat.male", "Kleinkind (0 bis 5 Jahre) ohne Sitzplatz, männlich entfernen")}
									</span>
								</button>
								<span>{this.counterSmallChildrenMale}</span>
								<button class={this.maxReachedPersons ? 'disabled' : ''}
										onClick={(e) => this.countUp(e, PassangerType.SmallChild, "male")}>
									<add-icon aria-hidden="true" />
									<span class="screen-reader-text">
										{i18n.getMessage("passengers.add.smallChildrenWithoutSeat.male", "Kleinkind (0 bis 5 Jahre) ohne Sitzplatz, männlich hinzufügen")}
									</span>
								</button>
							</div>
						</div>
					</div>
					{this.renderSelectedUsersByUsertype(PassangerType.SmallChild)}
				</div>
				<div class="counter">
					<div class="counter__wrapper">
						<div>
							<span class="counter__label">{i18n.getMessage('passengers.bikes', 'Fahrräder')}</span>
						</div>
						<div class="counter__controler">
							<button class={this.counterBikes === 0 ? 'disabled' : ''}
									onClick={(event) => this.countDown(event, PassangerType.Bike)}>
								<minus-icon aria-hidden="true" />
								<span class="screen-reader-text">
									{i18n.getMessage("passengers.remove.bike", "Fahrrad entfernen")}
								</span>
							</button>
							<span>{this.counterBikes}</span>
							<button class={this.maxReachedBikes ? 'disabled' : ''}
									onClick={(e) => this.countUp(e, PassangerType.Bike)}>
								<add-icon aria-hidden="true" />
								<span class="screen-reader-text">
									{i18n.getMessage("passengers.add.bike", "Fahrrad hinzufügen")}
								</span>
							</button>
						</div>
					</div>
				</div>

				<nj-dialog title={i18n.getMessage('passengers.addReductionCards', 'Ermäßigungskarten')}
						   class="js-dialog-reduction-cards">
					<nj-reduction-cards class="js-reduction-cards"/>
				</nj-dialog>

				<nj-dialog title={i18n.getMessage('passengers.addChallengedOptions', 'Reisen mit Behinderung')}
						   class="js-dialog-challenged-options">
					<nj-challenged-options cards={this.cardsChallenged} class="js-challenged-options"/>
				</nj-dialog>

				<nj-dialog title={i18n.getMessage("passengers.dialogues.maximum-number-of-passengers-exceeded.title")}
						   id="dialogueMaximumNumberOfPassengersReached">
					<p>{i18n.getMessage("passengers.dialogues.maximum-number-of-passengers-exceeded.message")}</p>
				</nj-dialog>

				<div style={{textAlign: "right"}}>
					<button class="button--apply-changes" onClick={() => this.hide()}>
						{i18n.getMessage('passengers.done', 'Fertig')}
						<arrow-right-icon></arrow-right-icon>
					</button>
				</div>
			</div>
		);
	}
}
